import type { OutreachAudienceSource, OutreachImportSource, OutreachRow } from '@discovr/types';
import { CreatorCell, type ColumnDef } from '../../../components/common/DataTable';
import { CrmChannelCell } from './CrmChannelCell';
import { formatActivityTime } from './crmFormat';
import { hasThread } from './handoffStates';

const UNKNOWN = '—';

export const creatorName = (row: Pick<OutreachRow, 'displayName' | 'username'>): string => row.displayName || row.username;

/** A clickable row's name: "Open conversation with …" once there is a thread, otherwise why the row does nothing. */
export const crmRowLabel = (row: Pick<OutreachRow, 'displayName' | 'username' | 'outreachState'>): string =>
  hasThread(row) ? `Open conversation with ${creatorName(row)}` : `${creatorName(row)}, not contacted yet`;

const AUDIENCE_SOURCE_LABEL: Record<OutreachAudienceSource, string> = {
  discovery: 'Discovery estimate',
  import: 'Import',
  connected: 'Connected account',
};

const IMPORT_SOURCE_LABEL: Partial<Record<OutreachImportSource, string>> = {
  sheet: 'Sheet import',
  paste: 'Pasted handles',
};

/** An imported creator is named by how it came in when the row says so; a bare import is plain "Import". */
export function audienceSourceLabel(source: OutreachAudienceSource | null | undefined, importSource?: OutreachImportSource): string {
  if (source === 'import' && importSource) return IMPORT_SOURCE_LABEL[importSource] ?? AUDIENCE_SOURCE_LABEL.import;
  return source ? AUDIENCE_SOURCE_LABEL[source] : UNKNOWN;
}

/** The creator column every CRM list opens with: avatar, name, @handle. */
export function creatorColumn<T extends Pick<OutreachRow, 'displayName' | 'username' | 'avatarUrl'>>(): ColumnDef<T> {
  return {
    id: 'creator',
    header: 'Creator',
    minWidth: 220,
    maxWidth: 280,
    alwaysVisible: true,
    cell: (row) => <CreatorCell name={creatorName(row)} handle={row.username} avatarUrl={row.avatarUrl} />,
  };
}

export const channelColumn: ColumnDef<OutreachRow> = {
  id: 'channel',
  header: 'Channel',
  minWidth: 90,
  cell: (row) => <CrmChannelCell hasContact={row.hasContact} />,
};

export const lastActivityColumn: ColumnDef<OutreachRow> = {
  id: 'last_activity',
  header: 'Last activity',
  minWidth: 120,
  cell: (row) => <span className="text-body-sm-regular text-foreground">{formatActivityTime(row.lastActivityAt)}</span>,
};
